import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Button, Space, Typography } from 'antd';
import { useAuth } from '@/hooks/useAuth';
import { canSee } from '@/lib/role';
import type { Role } from '@/lib/role';

interface AuthGuardProps {
  children: ReactNode;
}

/** 无 token → 跳 /login。 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return <>{children}</>;
}

// Sprint42-hotfix:子门户角色直接访问业务页(/dashboard 等)被 403 时,
// 给"返回我的门户"按钮,避免用户卡死(LoginPage 也加了同样的子门户保护)
export function roleHomePath(role: Role | null | undefined): string {
  if (role === 'enterprise') return '/me/enterprise';
  if (role === 'mentor') return '/me/mentor';
  if (role === 'investor') return '/me/investor';
  if (role === 'staff' || role === 'tenant_admin' || role === 'manager') return '/my-todos';
  return '/dashboard';
}

/**
 * Sprint47-fix:根路由 `/` 按角色感知跳转。
 * 之前写死 Navigate to /dashboard,导致 enterprise 直接访问根 URL 被弹 403。
 */
export function RoleAwareLanding() {
  const { role } = useAuth();
  return <Navigate to={roleHomePath(role)} replace />;
}

interface RoleGuardProps {
  /** menu key（与 MENU_VISIBILITY 对齐）；或直接给允许的角色列表 */
  menuKey?: string;
  allow?: Role[];
  children: ReactNode;
}

/** 角色不匹配 → 子门户角色静默跳主页;其他角色 403 + 返回按钮。 */
export function RoleGuard({ menuKey, allow, children }: RoleGuardProps) {
  const { role } = useAuth();
  const navigate = useNavigate();

  let permitted = true;
  if (menuKey) permitted = canSee(menuKey, role);
  else if (allow) permitted = !!role && allow.includes(role);

  if (!permitted) {
    // Sprint47-fix:子门户角色(enterprise/mentor/investor)没业务概念,
    // 直接访问 /dashboard 等业务 URL 一律静默跳主页(而非显 403 卡死)。
    // 其他角色(staff/admin)保留 403 + 按钮 — 避免悄悄掩盖权限配置错误。
    const isSubportalRole =
      role === 'enterprise' || role === 'mentor' || role === 'investor';
    if (isSubportalRole) {
      return <Navigate to={roleHomePath(role)} replace />;
    }
    const home = roleHomePath(role);
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Typography.Title level={2} style={{ marginBottom: 8 }}>403</Typography.Title>
        <Typography.Paragraph>当前角色无权访问该页面。</Typography.Paragraph>
        <Space>
          <Button type="primary" onClick={() => navigate(home, { replace: true })}>
            返回我的主页
          </Button>
          <Button onClick={() => navigate(-1)}>返回上一页</Button>
        </Space>
      </div>
    );
  }
  return <>{children}</>;
}
